import axios from 'axios';

// digiCamControl API 기본 설정
// Vite 프록시를 통해 CORS 회피
const CAMERA_API_BASE_URL = '/camera-api';

// 카메라 API 인스턴스
const cameraApi = axios.create({
  baseURL: CAMERA_API_BASE_URL,
  timeout: 10000, // 10초 타임아웃
});

export interface CameraFile {
  Name: string;
  FileName: string;
  ShortName: string;
  FileDate: string;
  Format: string;
  Iso: string;
  E: string;
  F: string;
  FocalLength: string;
}

export interface CameraSession {
  Name: string;
  Folder: string;
  Counter: number;
  Files: CameraFile[];
}

/**
 * 세션에서 가장 최근 촬영된 사진 파일명 가져오기
 */
export const getLatestCapturedImage = (session: CameraSession): string | null => {
  if (!session.Files || session.Files.length === 0) {
    return null;
  }
  // Files 배열의 마지막 항목이 가장 최근 촬영된 사진
  const latestFile = session.Files[session.Files.length - 1];
  return latestFile.Name;
};

/**
 * 카메라 서버 연결 상태 확인
 */
export const checkCameraConnection = async (): Promise<boolean> => {
  try {
    await cameraApi.get('/');
    return true;
  } catch (error) {
    console.error('Camera connection failed:', error);
    return false;
  }
};

/**
 * 현재 세션 정보 가져오기
 */
export const getCameraSession = async (): Promise<CameraSession> => {
  try {
    const response = await cameraApi.get('/session.json');
    return response.data;
  } catch (error) {
    console.error('Failed to get camera session:', error);
    throw error;
  }
};

/**
 * 사진 촬영 (셔터 트리거)
 */
export const capturePhoto = async (): Promise<void> => {
  try {
    await cameraApi.get('/', {
      params: {
        slc: 'capture',
      },
    });
  } catch (error) {
    console.error('Failed to capture photo:', error);
    throw error;
  }
};

/**
 * 촬영된 사진 다운로드
 * @param imageName - 이미지 파일명 (예: "IMG_0001.jpg")
 */
export const downloadPhoto = async (imageName: string): Promise<Blob> => {
  try {
    const response = await cameraApi.get(`/image/${imageName}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Failed to download photo:', error);
    throw error;
  }
};

/**
 * 썸네일 다운로드
 * @param imageName - 이미지 파일명
 * @param size - 'large' | 'small'
 */
export const downloadThumbnail = async (
  imageName: string,
  size: 'large' | 'small' = 'large'
): Promise<Blob> => {
  try {
    const response = await cameraApi.get(`/thumb/${size}/${imageName}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Failed to download thumbnail:', error);
    throw error;
  }
};

/**
 * 라이브뷰 숨기기
 */
export const hideLiveView = async (): Promise<void> => {
  try {
    await cameraApi.get('/', {
      params: {
        CMD: 'LiveViewWnd_Hide',
      },
    });
  } catch (error) {
    console.error('Failed to hide live view:', error);
    throw error;
  }
};

/**
 * 라이브뷰 표시
 */
export const showLiveView = async (): Promise<void> => {
  try {
    await cameraApi.get('/', {
      params: {
        CMD: 'LiveViewWnd_Show',
      },
    });
  } catch (error) {
    console.error('Failed to show live view:', error);
    throw error;
  }
};

/**
 * 카메라 설정 변경
 * @param param - 설정 파라미터명
 * @param value - 설정 값
 */
export const setCameraSetting = async (param: string, value: string): Promise<void> => {
  try {
    await cameraApi.get('/', {
      params: {
        slc: 'set',
        param1: param,
        param2: value,
      },
    });
  } catch (error) {
    console.error('Failed to set camera setting:', error);
    throw error;
  }
};

export default {
  checkCameraConnection,
  getCameraSession,
  capturePhoto,
  downloadPhoto,
  downloadThumbnail,
  hideLiveView,
  showLiveView,
  setCameraSetting,
  getLatestCapturedImage,
};
